/**
 * Pure chart generator functions for safety parameter visualization.
 * Each function takes register data and returns ECharts options.
 *
 * Adapted from operators/device-grid-settings/device-grid-settings.utils.ts
 * for use in the ConfigForm system.
 */
import { getStyleProp } from 'ngx-core-services';
import { EChartsOption } from 'echarts';

// ── Types ──────────────────────────────────────────────────────

export interface SafetyChartOutput {
  chartOptions: EChartsOption;
  /** Plain-text description — set by the host after translating from its own i18n keys */
  description?: string;
  legend?: { color: string; label: string }[];
}

export interface ProtectionPoint {
  label: string;
  value: number;        // Y-axis (V, Hz, mA)
  time: number;         // X-axis (ms)
  color: string;
}

// ── Shared Helpers & Premium Theme ──────────────────────────────

const FONT = 'Inter, system-ui, sans-serif';

const CHART_GRID = { top: 40, bottom: 32, left: 48, right: 48, containLabel: false };

/** Base tooltip style — glass-morphism with shadow */
const tooltipBase = {
  confine: true,
  backgroundColor: 'rgba(255, 255, 255, 0.96)',
  borderColor: 'rgba(0, 0, 0, 0.06)',
  borderWidth: 1,
  borderRadius: 8,
  padding: 0,
  shadowBlur: 16,
  shadowColor: 'rgba(0, 0, 0, 0.08)',
  shadowOffsetY: 4,
  extraCssText: 'backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px); max-width: 320px;',
  textStyle: { color: getStyleProp('--grey-400'), fontSize: 12, fontFamily: FONT },
};

const tooltipRow = (color: string, label: string, value: string) =>
  `<div style="display:flex;align-items:center;gap:8px;padding:4px 12px;font-size:12px;font-family:${FONT}">` +
  `<span style="width:8px;height:8px;min-width:8px;border-radius:2px;background:${color}"></span>` +
  `<span style="color:${getStyleProp('--grey-400')};flex:1">${label}</span>` +
  `<span style="font-weight:600;color:${getStyleProp('--grey-500')}">${value}</span></div>`;

const tooltipHeader = (text: string) =>
  `<div style="padding:6px 12px;background:${getStyleProp('--accent-100')};border-radius:8px 8px 0 0;` +
  `font-size:11px;font-weight:600;color:${getStyleProp('--accent')};font-family:${FONT};text-transform:uppercase;letter-spacing:0.5px">${text}</div>`;

/** Axis-triggered tooltip for line charts (P(U), P(f), cos phi, Q(U)) */
const lineChartTooltip = (xUnit: string, yUnit: string) => ({
  ...tooltipBase,
  trigger: 'axis' as const,
  axisPointer: {
    type: 'cross' as const,
    lineStyle: { color: getStyleProp('--grey-200'), width: 1, type: 'dashed' as const },
    crossStyle: { color: getStyleProp('--grey-200') },
    label: { show: false },
  },
  formatter: (params: any) => {
    if (!Array.isArray(params) || !params.length) { return ''; }
    const xVal = params[0].axisValue;
    let html = tooltipHeader(`${Number(xVal).toFixed(1)} ${xUnit}`);
    html += '<div style="padding:4px 0">';
    for (const p of params) {
      if (p.seriesName && p.seriesName !== 'series') {
        html += tooltipRow(p.color, p.seriesName, `${Number(p.value[1]).toFixed(2)} ${yUnit}`);
      }
    }
    html += '</div>';
    return html;
  },
});

/** Smooth entry animation */
const CHART_ANIMATION = {
  animation: true,
  animationDuration: 700,
  animationEasing: 'cubicOut' as const,
  animationDurationUpdate: 350,
  animationEasingUpdate: 'cubicInOut' as const,
};

const baseAxis = (name: string, opts: Record<string, any> = {}) => ({
  type: 'value' as const,
  boundaryGap: false,
  axisTick: { show: false },
  splitLine: {
    show: true,
    lineStyle: { color: getStyleProp('--grey-100'), width: 1, type: 'solid' as const },
  },
  nameTextStyle: {
    color: getStyleProp('--grey-400'),
    fontSize: 11,
    fontFamily: FONT,
    fontWeight: 600,
    padding: [0, 0, 0, 0],
  },
  axisLabel: {
    color: getStyleProp('--grey-300'),
    fontSize: 11,
    fontFamily: FONT,
    fontWeight: 500 as any,
    hideOverlap: true,
  },
  axisLine: {
    show: true,
    lineStyle: { color: getStyleProp('--grey-200'), width: 1 },
  },
  name,
  ...opts,
});

/** Convert hex color to rgba string */
const hexToRgba = (hex: string, alpha: number): string => {
  const match = hex.replace('#', '').match(/.{2}/g);
  if (!match) { return `rgba(0,0,0,${alpha})`; }
  const [r, g, b] = match.map(c => parseInt(c, 16));
  return `rgba(${r},${g},${b},${alpha})`;
};

const lineSeriesBase = (data: number[][], color: string, width = 2.5) => ({
  type: 'line' as const,
  symbol: 'circle',
  symbolSize: 5,
  showSymbol: false,
  lineStyle: {
    color,
    width,
    cap: 'round' as const,
    join: 'round' as const,
    shadowColor: hexToRgba(color, 0.2),
    shadowBlur: 6,
    shadowOffsetY: 3,
  },
  itemStyle: {
    color,
    borderColor: '#fff',
    borderWidth: 2,
  },
  emphasis: {
    focus: 'series' as const,
    lineStyle: { width: width + 1 },
    itemStyle: {
      shadowBlur: 8,
      shadowColor: hexToRgba(color, 0.3),
    },
  },
  data,
});

const areaSeriesBase = (data: number[][], color: string) => ({
  type: 'line' as const,
  symbol: 'none',
  lineStyle: { color: 'transparent', width: 0 },
  areaStyle: { opacity: 0.12 },
  data,
});

// ── 1. Protection Chart (Voltage / Frequency / DCI) ─────────

interface ProtectionChartOpts {
  yAxisLabel: string;       // 'V', 'Hz', 'mA'
  xAxisLabel: string;       // 'ms'
  nominalValue?: number;
  nominalLabel?: string;
}

/**
 * Scatter + area chart for protection thresholds.
 * Shows OV/UV or OF/UF levels with time thresholds.
 */
export function generateProtectionChart(
  points: ProtectionPoint[],
  nominal: { value: number; label: string } | undefined,
  opts: ProtectionChartOpts,
): SafetyChartOutput | null {
  if (!points.length) { return null; }

  const nominalVal = nominal?.value ?? 0;

  // Build area polygon data for the visual map
  const allSorted = [...points].sort((a, b) => a.value - b.value);
  const maxTime = Math.max(...points.map(p => p.time), 100) * 1.1;

  // Build series: each protection level as a scatter point + threshold line
  const series: any[] = [];
  const legendItems: { color: string; label: string }[] = [];

  // Area fill between protection levels
  const areaData: number[][] = [];
  allSorted.forEach((p, i) => {
    if (i === 0) { areaData.push([0, p.value]); }
    areaData.push([p.time, p.value]);
    if (i < allSorted.length - 1) {
      areaData.push([p.time, allSorted[i + 1].value]);
    }
    if (i === allSorted.length - 1) { areaData.push([0, p.value]); }
  });

  if (areaData.length) {
    series.push({
      ...areaSeriesBase(areaData, 'transparent'),
    });
  }

  // Nominal line
  if (nominal) {
    series.push({
      ...lineSeriesBase(
        [[0, nominal.value], [maxTime, nominal.value]],
        getStyleProp('--green-500'), 2,
      ),
      name: nominal.label,
      lineStyle: {
        color: getStyleProp('--green-500'),
        width: 2,
        type: 'dashed' as const,
      },
      tooltip: { show: false },
    });
    legendItems.push({ color: getStyleProp('--green-500'), label: nominal.label });
  }

  // Protection level points — large interactive markers that invite hover
  for (const p of points) {
    const color = p.color || getStyleProp('--orange-300');
    series.push({
      type: 'scatter' as const,
      name: p.label,
      data: [[p.time, p.value]],
      symbolSize: 14,
      cursor: 'pointer',
      itemStyle: {
        color,
        borderColor: '#fff',
        borderWidth: 2.5,
        shadowBlur: 8,
        shadowColor: hexToRgba(color, 0.4),
      },
      emphasis: {
        scale: 1.6,
        itemStyle: {
          shadowBlur: 14,
          shadowColor: hexToRgba(color, 0.5),
          borderWidth: 3,
        },
      },
      z: 10,
    });
    // Threshold reference line (horizontal dash to the point)
    series.push({
      ...lineSeriesBase([[0, p.value], [p.time, p.value]], color, 1),
      name: `${p.label} ref`,
      lineStyle: { color, width: 1, type: 'dotted' as const },
      symbol: 'none',
      tooltip: { show: false },
    });
    legendItems.push({ color, label: p.label });
  }

  // Visual map for colored areas
  const pieces: any[] = [];
  allSorted.forEach((item, i) => {
    const next = allSorted[i + 1];
    if (!next) { return; }
    const isAboveNominal = item.value >= nominalVal;
    const color = isAboveNominal ? (next.color || item.color) : (item.color || next.color);
    pieces.push({ gt: item.value, lt: next.value, color });
  });

  // Compute Y-axis range with margin so all points are clearly visible
  const allValues = points.map(p => p.value);
  if (nominalVal) { allValues.push(nominalVal); }
  const yMin = Math.min(...allValues);
  const yMax = Math.max(...allValues);
  const yMargin = (yMax - yMin) * 0.15 || 10;

  // Protection-specific tooltip: trigger on points, shows label + value + time
  const unit = opts.yAxisLabel;
  const protectionTooltip = {
    ...tooltipBase,
    trigger: 'item' as const,
    formatter: (p: any) => {
      if (!p.seriesName || p.seriesName.endsWith('ref')) { return ''; }
      const [time, value] = p.value;
      const name = p.seriesName;
      let html = tooltipHeader(name);
      html += '<div style="padding:6px 0">';
      html += tooltipRow(p.color, `Threshold`, `${Number(value).toFixed(1)} ${unit}`);
      html += tooltipRow(p.color, `Trip time`, `${Number(time).toFixed(0)} ms`);
      // Add contextual description
      const isOver = name.startsWith('OV') || name.startsWith('OF');
      const desc = isOver
        ? `Disconnects if ${unit === 'V' ? 'voltage' : unit === 'Hz' ? 'frequency' : 'current'} exceeds ${Number(value).toFixed(1)} ${unit} for ${Number(time).toFixed(0)} ms`
        : `Disconnects if ${unit === 'V' ? 'voltage' : unit === 'Hz' ? 'frequency' : 'current'} drops below ${Number(value).toFixed(1)} ${unit} for ${Number(time).toFixed(0)} ms`;
      html += `<div style="padding:4px 12px 6px;font-size:11px;color:${getStyleProp('--grey-300')};line-height:1.4;font-family:${FONT}">${desc}</div>`;
      html += '</div>';
      return html;
    },
  };

  const chartOptions: EChartsOption = {
    ...CHART_ANIMATION,
    tooltip: protectionTooltip,
    grid: CHART_GRID,
    xAxis: baseAxis(opts.xAxisLabel, { min: 0, max: Math.ceil(maxTime) }),
    yAxis: baseAxis(opts.yAxisLabel, {
      min: Math.floor(yMin - yMargin),
      max: Math.ceil(yMax + yMargin),
    }),
    series,
    visualMap: pieces.length ? {
      type: 'piecewise',
      show: false,
      dimension: 1,
      seriesIndex: 0,
      pieces,
    } : undefined,
  };

  return { chartOptions, legend: legendItems };
}

// ── 2. Full P(U) Derate Chart ───────────────────────────────

interface PUDerateParams {
  start: number;      // voltage where derating begins
  stop: number;       // voltage where derating reaches min power
  minPower: number;   // minimum power %
}

/**
 * Trapezoidal P(U) curve showing power vs voltage derating.
 * Supports both overvoltage (right side) and undervoltage (left side).
 */
export function generateFullPUDerateChart(
  overVoltage: PUDerateParams | null,
  underVoltage: PUDerateParams | null,
  opts: { title?: string },
): SafetyChartOutput | null {
  if (!overVoltage && !underVoltage) { return null; }

  const data: number[][] = [];
  const legendItems: { color: string; label: string }[] = [];
  const ovColor = getStyleProp('--red-300');
  const uvColor = getStyleProp('--blue-300');

  // Determine voltage range for buffer
  const allVoltages: number[] = [];
  if (underVoltage) { allVoltages.push(underVoltage.stop, underVoltage.start); }
  if (overVoltage) { allVoltages.push(overVoltage.start, overVoltage.stop); }
  const voltRange = Math.max(...allVoltages) - Math.min(...allVoltages);
  const buffer = voltRange * 0.1;

  // Build curve left to right
  if (underVoltage) {
    data.push(
      [underVoltage.stop - buffer, underVoltage.minPower],
      [underVoltage.stop, underVoltage.minPower],
      [underVoltage.start, 100],
    );
    legendItems.push({ color: uvColor, label: 'UV Derate' });
  }

  // Flat 100% region
  const leftFlat = underVoltage ? underVoltage.start : (overVoltage!.start - buffer);
  const rightFlat = overVoltage ? overVoltage.start : (underVoltage!.stop + buffer);
  if (!underVoltage) { data.push([leftFlat, 100]); }
  data.push([rightFlat, 100]);

  if (overVoltage) {
    data.push(
      [overVoltage.start, 100],
      [overVoltage.stop, overVoltage.minPower],
      [overVoltage.stop + buffer, overVoltage.minPower],
    );
    legendItems.push({ color: ovColor, label: 'OV Derate' });
  }

  const xMin = data[0][0];
  const xMax = data[data.length - 1][0];

  const chartOptions: EChartsOption = {
    ...CHART_ANIMATION,
    tooltip: lineChartTooltip('V', '%Pn'),
    grid: CHART_GRID,
    title: opts.title ? {
      text: opts.title,
      top: 8, left: 'center',
      textStyle: { color: getStyleProp('--grey-400'), fontSize: 13, fontWeight: 500, fontFamily: FONT },
    } : undefined,
    xAxis: baseAxis('V', { min: Math.floor(xMin), max: Math.ceil(xMax), scale: true }),
    yAxis: baseAxis('%Pn', { min: -10, max: 110 }),
    series: [
      {
        name: 'P(U)',
        type: 'line',
        symbol: 'circle',
        symbolSize: 5,
        data,
        lineStyle: { color: getStyleProp('--accent'), width: 2 },
        itemStyle: { color: getStyleProp('--accent') },
        areaStyle: { color: getStyleProp('--accent'), opacity: 0.08 },
      },
    ],
  };

  return { chartOptions, legend: legendItems };
}

// ── 3. FSM Frequency Derate P(f) Chart ──────────────────────

interface FreqDerateParams {
  startFreq: number;   // Hz — where derating begins
  endFreq?: number;    // Hz — range mode end (null for slope mode)
  slope?: number;      // %Pn/Hz — slope mode gradient
  ratedFreq: number;   // Hz — rated frequency (50 or 60)
}

/**
 * P(f) curve for frequency-sensitive mode (LFSM-O / LFSM-U).
 * Two modes: Slope (gradient) and Range (start/end frequency).
 */
export function generateFreqDerateChart(
  overFreq: FreqDerateParams | null,
  underFreq: FreqDerateParams | null,
  mode: 'slope' | 'range',
): SafetyChartOutput | null {
  if (!overFreq && !underFreq) { return null; }

  const series: any[] = [];
  const legendItems: { color: string; label: string }[] = [];
  const ofColor = getStyleProp('--red-300');
  const ufColor = getStyleProp('--blue-300');

  const rated = overFreq?.ratedFreq ?? underFreq?.ratedFreq ?? 50;
  let xMin = rated - 3;
  let xMax = rated + 3;

  // Over-frequency (LFSM-O): power reduces above startFreq
  if (overFreq) {
    const pts: number[][] = [];
    pts.push([overFreq.startFreq, 100]);
    if (mode === 'range' && overFreq.endFreq) {
      pts.push([overFreq.endFreq, 0]);
      xMax = Math.max(xMax, overFreq.endFreq + 0.5);
    } else if (mode === 'slope' && (overFreq.slope ?? 0) > 0) {
      const endF = overFreq.startFreq + (100 / overFreq.slope!);
      pts.push([endF, 0]);
      xMax = Math.max(xMax, endF + 0.5);
    }
    const ofLabel = `LFSM-O (${mode === 'slope' ? overFreq.slope + ' %/Hz' : 'range'})`;
    series.push({ ...lineSeriesBase(pts, ofColor, 2), name: ofLabel, areaStyle: { color: ofColor, opacity: 0.1 } });
    legendItems.push({ color: ofColor, label: ofLabel });
  }

  // Under-frequency (LFSM-U): power reduces below startFreq
  if (underFreq) {
    const pts: number[][] = [];
    if (mode === 'range' && underFreq.endFreq) {
      pts.push([underFreq.endFreq, 0]);
      xMin = Math.min(xMin, underFreq.endFreq - 0.5);
    } else if (mode === 'slope' && (underFreq.slope ?? 0) > 0) {
      const endF = underFreq.startFreq - (100 / underFreq.slope!);
      pts.push([endF, 0]);
      xMin = Math.min(xMin, endF - 0.5);
    }
    pts.push([underFreq.startFreq, 100]);
    const ufLabel = `LFSM-U (${mode === 'slope' ? underFreq.slope + ' %/Hz' : 'range'})`;
    series.push({ ...lineSeriesBase(pts, ufColor, 2), name: ufLabel, areaStyle: { color: ufColor, opacity: 0.1 } });
    legendItems.push({ color: ufColor, label: ufLabel });
  }

  // Rated frequency reference line
  series.push({
    ...lineSeriesBase([[rated, 0], [rated, 100]], getStyleProp('--green-500'), 1),
    name: `fn = ${rated} Hz`,
    lineStyle: { color: getStyleProp('--green-500'), width: 1, type: 'dashed' as const },
    symbol: 'none',
    tooltip: { show: false },
  });
  legendItems.push({ color: getStyleProp('--green-500'), label: `fn = ${rated} Hz` });

  const chartOptions: EChartsOption = {
    ...CHART_ANIMATION,
    tooltip: lineChartTooltip('Hz', '%Pn'),
    grid: CHART_GRID,
    xAxis: baseAxis('Hz', { min: Math.floor(xMin * 100) / 100, max: Math.ceil(xMax * 100) / 100, scale: true }),
    yAxis: baseAxis('%Pn', { min: -5, max: 110 }),
    series,
  };

  return { chartOptions, legend: legendItems };
}

// ── 4. cos phi(P) Chart ─────────────────────────────────────

interface CosPhiPoint {
  power: number;     // %Pn
  cosPhi: number;    // cos phi value (positive = overexcited, negative = underexcited)
}

/**
 * cos phi(P) curve — 4-point piecewise showing power factor vs active power.
 */
export function generateCosPhiPChart(
  points: CosPhiPoint[],
): SafetyChartOutput | null {
  if (points.length < 2) { return null; }

  const sorted = [...points].sort((a, b) => a.power - b.power);
  const data = sorted.map(p => [p.power, p.cosPhi]);
  const color = getStyleProp('--accent');

  // Compute Y range with margin — cos phi values are typically -1 to 1
  const cosValues = sorted.map(p => p.cosPhi);
  const cosMin = Math.min(...cosValues);
  const cosMax = Math.max(...cosValues);
  const cosMargin = Math.max((cosMax - cosMin) * 0.2, 0.05);

  const chartOptions: EChartsOption = {
    ...CHART_ANIMATION,
    tooltip: lineChartTooltip('%Pn', 'cos \u03C6'),
    grid: CHART_GRID,
    xAxis: baseAxis('%Pn', { min: 0, max: 100 }),
    yAxis: baseAxis('cos \u03C6', {
      min: Math.floor((cosMin - cosMargin) * 100) / 100,
      max: Math.ceil((cosMax + cosMargin) * 100) / 100,
    }),
    series: [{
      ...lineSeriesBase(data, color, 2),
      name: 'cos \u03C6(P)',
      areaStyle: { color, opacity: 0.08 },
    }],
  };

  return {
    chartOptions,
    legend: [{ color, label: 'cos \u03C6(P)' }],
  };
}

// ── 5. Q(U) Gradient Chart ──────────────────────────────────

interface QUGradientParams {
  u1: number;  // %Un — plateau overexcited
  u2: number;  // %Un — knee overexcited
  u3: number;  // %Un — knee underexcited
  u4: number;  // %Un — plateau underexcited
  qMax: number;       // %Pn — max overexcited (positive)
  qMin: number;       // %Pn — max underexcited (negative)
  qStart: number;     // %Pn — Q at rated voltage
  offset: number;     // %Qmax — global offset
}

/**
 * Q(U) gradient curve — continuous reactive power vs voltage.
 */
export function generateQUGradientChart(
  params: QUGradientParams,
): SafetyChartOutput | null {
  const { u1, u2, u3, u4, qMax, qMin, qStart, offset } = params;
  const qOffset = offset;

  const data: number[][] = [
    [u1, qMax + qOffset],
    [u2, qStart + qOffset],
    [u3, qStart + qOffset],
    [u4, qMin + qOffset],
  ];

  // Extended ends
  const buffer = (u4 - u1) * 0.1;
  data.unshift([u1 - buffer, qMax + qOffset]);
  data.push([u4 + buffer, qMin + qOffset]);

  const lagColor = getStyleProp('--blue-300');
  const leadColor = getStyleProp('--red-300');

  // Compute ranges with margin
  const allQ = data.map(d => d[1]);
  const qYMin = Math.min(...allQ, 0);
  const qYMax = Math.max(...allQ, 0);
  const qYMargin = (qYMax - qYMin) * 0.15 || 5;

  const chartOptions: EChartsOption = {
    ...CHART_ANIMATION,
    tooltip: lineChartTooltip('%Un', 'Q %Pn'),
    grid: CHART_GRID,
    xAxis: baseAxis('%Un', { min: Math.floor(u1 - buffer), max: Math.ceil(u4 + buffer) }),
    yAxis: baseAxis('Q %Pn', { min: Math.floor(qYMin - qYMargin), max: Math.ceil(qYMax + qYMargin) }),
    series: [
      { ...lineSeriesBase(data, getStyleProp('--accent'), 2), name: 'Q(U)' },
      // Zero line
      {
        ...lineSeriesBase([[u1 - buffer, 0], [u4 + buffer, 0]], getStyleProp('--grey-200'), 1),
        lineStyle: { color: getStyleProp('--grey-200'), width: 1, type: 'dashed' as const },
        symbol: 'none',
      },
    ],
  };

  return {
    chartOptions,
    legend: [
      { color: lagColor, label: 'Q+ (overexcited)' },
      { color: leadColor, label: 'Q- (underexcited)' },
    ],
  };
}

// ── 6. Q(U) Step Chart ──────────────────────────────────────

interface QUStepParams {
  u1Rising: number;   // %Un — switch-on (falling) overexcited
  u2Rising: number;   // %Un — switch-off (falling)
  u3Falling: number;  // %Un — switch-off (rising)
  u4Falling: number;  // %Un — switch-on (rising)
  qMax: number;       // %Pn — max reactive power
}

/**
 * Q(U) step chart with hysteresis — different ON/OFF voltage thresholds.
 */
export function generateQUStepChart(
  params: QUStepParams,
): SafetyChartOutput | null {
  const { u1Rising, u2Rising, u3Falling, u4Falling, qMax } = params;

  const buffer = Math.max((u4Falling - u1Rising) * 0.1, 2);
  const risingColor = getStyleProp('--blue-300');
  const fallingColor = getStyleProp('--red-300');

  // Rising (voltage going up): steps from +qMax to 0 to -qMax
  const risingData: number[][] = [
    [u1Rising - buffer, qMax],
    [u1Rising, qMax],
    [u2Rising, qMax],
    [u2Rising, 0],
    [u3Falling, 0],
    [u3Falling, -qMax],
    [u4Falling, -qMax],
    [u4Falling + buffer, -qMax],
  ];

  // Falling (voltage going down): different thresholds
  const fallingData: number[][] = [
    [u1Rising - buffer, qMax],
    [u1Rising, qMax],
    [u3Falling, qMax],
    [u3Falling, 0],
    [u2Rising, 0],
    [u2Rising, -qMax],
    [u4Falling, -qMax],
    [u4Falling + buffer, -qMax],
  ];

  // Explicit ranges with symmetric Q axis and voltage margin
  const qMargin = qMax * 0.2 || 5;

  const chartOptions: EChartsOption = {
    ...CHART_ANIMATION,
    tooltip: lineChartTooltip('%Un', 'Q %Pn'),
    grid: CHART_GRID,
    xAxis: baseAxis('%Un', { min: Math.floor(u1Rising - buffer), max: Math.ceil(u4Falling + buffer) }),
    yAxis: baseAxis('Q %Pn', { min: Math.floor(-qMax - qMargin), max: Math.ceil(qMax + qMargin) }),
    series: [
      { ...lineSeriesBase(risingData, risingColor, 2), name: 'Rising' },
      {
        ...lineSeriesBase(fallingData, fallingColor, 2),
        lineStyle: { color: fallingColor, width: 2, type: 'dashed' as const },
        name: 'Falling',
      },
      // Zero line
      {
        ...lineSeriesBase(
          [[u1Rising - buffer, 0], [u4Falling + buffer, 0]],
          getStyleProp('--grey-200'), 1,
        ),
        lineStyle: { color: getStyleProp('--grey-200'), width: 1, type: 'dashed' as const },
        symbol: 'none',
      },
    ],
  };

  return {
    chartOptions,
    legend: [
      { color: risingColor, label: 'Rising (V\u2191)' },
      { color: fallingColor, label: 'Falling (V\u2193)' },
    ],
  };
}

// ── 7. VRT (LVRT / OVRT) Envelope Chart ─────────────────────

interface VRTPoint {
  voltage: number;  // %Un
  time: number;     // ms
}

interface VRTParams {
  entryVoltage: number;  // %Un
  exitVoltage: number;   // %Un
  points: VRTPoint[];    // 4 time-voltage points (ordered by time)
  type: 'LVRT' | 'OVRT';
}

/**
 * Voltage Ride-Through envelope — shows the fault ride-through capability.
 * LVRT: inverter must stay connected if voltage stays above the curve.
 * OVRT: inverter must stay connected if voltage stays below the curve.
 */
export function generateVRTChart(
  params: VRTParams,
): SafetyChartOutput | null {
  const { entryVoltage, exitVoltage, points, type } = params;
  if (!points.length) { return null; }

  const sorted = [...points].sort((a, b) => a.time - b.time);
  const isLVRT = type === 'LVRT';
  const color = isLVRT ? getStyleProp('--blue-300') : getStyleProp('--red-300');
  const areaColor = isLVRT ? getStyleProp('--blue-200') : getStyleProp('--red-200');

  const maxTime = Math.max(...sorted.map(p => p.time)) * 1.2;

  // Build envelope line: entry -> points -> exit -> extend
  const envelopeData: number[][] = [];

  // Start at time 0 with the first point's voltage (or entry voltage)
  envelopeData.push([0, sorted[0].voltage]);

  // Add each point (staircase: horizontal then vertical)
  for (let i = 0; i < sorted.length; i++) {
    const p = sorted[i];
    if (i > 0) {
      // Horizontal line from previous time to current time at previous voltage
      envelopeData.push([p.time, sorted[i - 1].voltage]);
    }
    envelopeData.push([p.time, p.voltage]);
  }

  // Extend to max time at last voltage
  envelopeData.push([maxTime, sorted[sorted.length - 1].voltage]);

  // Entry voltage reference line
  const entryLine: number[][] = [[0, entryVoltage], [maxTime, entryVoltage]];
  // Exit voltage reference line
  const exitLine: number[][] = [[0, exitVoltage], [maxTime, exitVoltage]];

  const series: any[] = [
    // Envelope curve
    {
      ...lineSeriesBase(envelopeData, color, 2),
      name: `${type} Envelope`,
      areaStyle: {
        color: areaColor,
        opacity: 0.2,
        origin: isLVRT ? 'start' : 'end',
      },
    },
    // Entry voltage line
    {
      ...lineSeriesBase(entryLine, getStyleProp('--orange-300'), 1),
      name: `Entry (${entryVoltage}%)`,
      lineStyle: { color: getStyleProp('--orange-300'), width: 1, type: 'dashed' as const },
      symbol: 'none',
      tooltip: { show: false },
    },
    // Exit voltage line
    {
      ...lineSeriesBase(exitLine, getStyleProp('--green-500'), 1),
      name: `Exit (${exitVoltage}%)`,
      lineStyle: { color: getStyleProp('--green-500'), width: 1, type: 'dashed' as const },
      symbol: 'none',
      tooltip: { show: false },
    },
  ];

  const chartOptions: EChartsOption = {
    ...CHART_ANIMATION,
    tooltip: lineChartTooltip('ms', '%Un'),
    grid: CHART_GRID,
    xAxis: baseAxis('ms', { min: 0, max: Math.ceil(maxTime) }),
    yAxis: baseAxis('%Un', { min: isLVRT ? 0 : 90, max: isLVRT ? 110 : 150 }),
    series,
  };

  const legend = [
    { color, label: `${type} Envelope` },
    { color: getStyleProp('--orange-300'), label: `Entry (${entryVoltage}%)` },
    { color: getStyleProp('--green-500'), label: `Exit (${exitVoltage}%)` },
  ];

  return { chartOptions, legend };
}
